import CitationPill from 'ai-components-ui-library/components/citation/CitationPill';
import InlineCitation from 'ai-components-ui-library/components/citation/InlineCitation';
import InlineFeedbackExpandable from 'ai-components-ui-library/components/feedback/InlineFeedbackExpandable';
import { BREEZE_TOOLTIP_DELAY } from 'chatspot-core/constants/Delays';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { CrmCitationTypename } from 'chatspot-core/types/Sources';
import { getSourceBadges, getSourceDisplayName, getSourceIcon } from 'chatspot-core/utils/sourcesUtils';
import { Image } from 'foundations-components/transitional/components/Image';
import { useTheme } from 'foundations-theming-specialty/utils';
import { useMemo, useRef } from 'react';
import { getCrmCitationObjectNameAndIcon } from '../../../utils/citationUtils';
import { getLocationWithinDocument } from '../../sources/getLocationWithinDocument';
import { getSourceUrl } from '../../sources/getSourceUrl';
import CrmCitationIcon from './CrmCitationIcon';
import { isChatMessageCitation, isMemoryCitation } from './citationUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POSITIVE_FEEDBACK = 1;
const NEGATIVE_FEEDBACK = -1;

// TEMPORARY solution for INBOUND
// This allows us to add specific UI for CRM citations
const isCrmCitation = citation => {
  return citation.__typename === CrmCitationTypename;
};
const Citation = ({
  citation,
  displayNumber,
  message,
  onCitationFeedback,
  onCitationClick,
  onCitationOpen,
  handledCitationTypes,
  onCitationOpened,
  onMessageCitationClick,
  isMessageCitationAvailable
}) => {
  var _citation$feedback;
  const theme = useTheme();
  const messageId = message.id;
  const threadId = message.threadId;
  const iconConfig = getSourceIcon(citation);
  const isMemoryCitationType = isMemoryCitation(citation);
  const isMessageCitationType = isChatMessageCitation(citation);
  const messageCitation = isChatMessageCitation(citation) ? citation : null;
  const memoryText = typeof (citation === null || citation === void 0 ? void 0 : citation.memoryText) === 'string' ? citation.memoryText : undefined;
  const badges = useMemo(() => {
    const sourceBadges = getSourceBadges(citation);
    if (isMemoryCitationType) {
      return sourceBadges;
    }
    const memorySourcesCount = typeof (citation === null || citation === void 0 ? void 0 : citation.sourcesCount) === 'number' ? citation.sourcesCount : null;
    if (memorySourcesCount && memorySourcesCount > 0) {
      return [...sourceBadges, I18n.text('aiComponentsUiLibrary.components.sourcesButton', {
        count: memorySourcesCount
      })];
    }
    return sourceBadges;
  }, [citation, isMemoryCitationType]);
  const isCrmObjectCitation = isCrmCitation(citation);
  const citationId = citation.id || `SEARCH_RESULT_${displayNumber}`;
  const currentFeedbackPolarityRef = useRef((_citation$feedback = citation.feedback) === null || _citation$feedback === void 0 ? void 0 : _citation$feedback.polarity);
  const getInitialVote = () => {
    if (!currentFeedbackPolarityRef.current) return undefined;
    return currentFeedbackPolarityRef.current === 'POSITIVE' ? POSITIVE_FEEDBACK : NEGATIVE_FEEDBACK;
  };
  const {
    sourceName,
    icon
  } = useMemo(() => {
    if (isCrmObjectCitation) {
      var _citation$objectTypeI, _citation$objectTypeI2, _citation$objectId;
      const {
        sourceName: sourceNameToUse,
        icon: IconToUse
      } = getCrmCitationObjectNameAndIcon({
        objectTypeId: (_citation$objectTypeI = citation.objectTypeId) !== null && _citation$objectTypeI !== void 0 ? _citation$objectTypeI : ''
      });
      return {
        sourceName: sourceNameToUse,
        icon: /*#__PURE__*/_jsx(CrmCitationIcon, {
          size: "400",
          avatarSize: "sm",
          padding: "100",
          defaultIcon: IconToUse,
          objectTypeId: (_citation$objectTypeI2 = citation.objectTypeId) !== null && _citation$objectTypeI2 !== void 0 ? _citation$objectTypeI2 : '',
          objectId: (_citation$objectId = citation.objectId) !== null && _citation$objectId !== void 0 ? _citation$objectId : ''
        })
      };
    }
    if (isMemoryCitationType) {
      return {
        sourceName: I18n.text('chatspotComponents.sections.memoryCitations.memoryLabel'),
        icon: null
      };
    }
    if (isMessageCitationType) {
      const sourceCreatedAt = messageCitation === null || messageCitation === void 0 ? void 0 : messageCitation.sourceCreatedAt;
      return {
        sourceName: sourceCreatedAt ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "chatspotComponents.sections.messageCitations.messageLabel"
          }), ' · ', /*#__PURE__*/_jsx(FormattedDateTime, {
            value: new Date(sourceCreatedAt),
            format: "LL"
          })]
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "chatspotComponents.sections.messageCitations.messageLabel"
        }),
        icon: null
      };
    }
    return {
      sourceName: getSourceDisplayName(citation),
      icon: iconConfig ? /*#__PURE__*/_jsx(Image, {
        src: iconConfig.url,
        alt: iconConfig.alt,
        width: theme.space['400'],
        height: theme.space['400']
      }) : null
    };
  }, [citation, messageCitation, isCrmObjectCitation, iconConfig, theme, isMemoryCitationType, isMessageCitationType]);
  const sourceUrl = useMemo(() => {
    const mappedSource = {
      id: citation.id || citationId,
      primaryText: citation.title || '',
      secondaryText: getSourceDisplayName(citation),
      data: citation
    };
    return getSourceUrl(mappedSource);
  }, [citation, citationId]);
  const handleSourceClick = () => {
    onCitationClick === null || onCitationClick === void 0 || onCitationClick(citation);
    if (isMemoryCitationType) return;
    if (isMessageCitationType) {
      onMessageCitationClick === null || onMessageCitationClick === void 0 || onMessageCitationClick(citation);
      return;
    }
    const shouldOverride = onCitationOpen && (handledCitationTypes === null || handledCitationTypes === void 0 ? void 0 : handledCitationTypes.includes(citation.__typename));
    if (shouldOverride) {
      onCitationOpen(citation);
    } else if (sourceUrl) {
      window.open(sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };
  const handleCitationOpened = () => {
    onCitationOpened === null || onCitationOpened === void 0 || onCitationOpened(citation);
  };
  const shouldShowFeedback = onCitationFeedback && messageId && threadId;
  const isThreadUnavailable = isMessageCitationType && isMessageCitationAvailable !== undefined && (!messageCitation.threadId || isMessageCitationAvailable(messageCitation) === false);
  if (isThreadUnavailable) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: unescapedText('chatspotComponents.sections.messageCitations.threadUnavailable'),
      delay: BREEZE_TOOLTIP_DELAY,
      placement: "top",
      children: /*#__PURE__*/_jsx(CitationPill, {
        label: displayNumber,
        disabled: true
      })
    });
  }
  const citationTitle = isMemoryCitationType && memoryText ? memoryText : citation.title || '';
  const location = getLocationWithinDocument(citation);
  return /*#__PURE__*/_jsx(InlineCitation, {
    label: displayNumber,
    title: citationTitle,
    locationText: location,
    sourceName: sourceName,
    isCrmObjectCitation: isCrmObjectCitation,
    badges: badges,
    icon: icon || undefined,
    onSourceClick: handleSourceClick,
    onCitationOpened: handleCitationOpened,
    feedbackComponent: shouldShowFeedback ? /*#__PURE__*/_jsx(InlineFeedbackExpandable, {
      initialVote: getInitialVote(),
      onPositiveFeedback: () => {
        currentFeedbackPolarityRef.current = 'POSITIVE';
        onCitationFeedback(citation, messageId.toString(), threadId, {
          polarity: 'POSITIVE',
          tags: [],
          comment: undefined
        });
      },
      onNegativeFeedback: ({
        options,
        feedbackMessage
      }) => {
        currentFeedbackPolarityRef.current = 'NEGATIVE';
        onCitationFeedback(citation, messageId.toString(), threadId, {
          polarity: 'NEGATIVE',
          tags: options !== null && options !== void 0 ? options : [],
          comment: feedbackMessage
        });
      },
      onRemoveFeedback: () => {
        const citationWithCurrentFeedback = Object.assign({}, citation, {
          feedback: currentFeedbackPolarityRef.current ? {
            polarity: currentFeedbackPolarityRef.current,
            tags: []
          } : undefined
        });
        currentFeedbackPolarityRef.current = undefined;
        onCitationFeedback(citationWithCurrentFeedback, messageId.toString(), threadId);
      }
    }) : undefined
  });
};
export default Citation;